import { useCallback, useEffect, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { getRuntime } from './_mode'
import type { WorkspaceEventSummary } from './types'
import { api, ApiError } from '../api/client'
import { captureHookFailure, recordHookOutcome, withSpan } from '../observability/sentry'

export function useWorkspaceEvents(projectId: string | undefined, limit = 20) {
  const [events, setEvents] = useState<WorkspaceEventSummary[]>([])
  const [workspaceIds, setWorkspaceIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!projectId) {
      setEvents([])
      setWorkspaceIds([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const { live } = getRuntime()
    if (!live) {
      setEvents([])
      setWorkspaceIds([])
      setLoading(false)
      recordHookOutcome('useWorkspaceEvents', 'demo_fallback', {
        route: '/api/workspace/events',
        rows: 0,
      })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.workspace.events({ project_id: projectId, limit })
      setEvents(result.events || [])
      setWorkspaceIds(result.workspace_ids || [])
      recordHookOutcome('useWorkspaceEvents', 'live_success', {
        route: '/api/workspace/events',
        rows: result.events?.length || 0,
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      const apiErr = err as ApiError
      setEvents([])
      setWorkspaceIds([])
      setError(apiErr?.message || 'workspace_events_failed')
      captureHookFailure('useWorkspaceEvents', 'live_error', err, {
        route: '/api/workspace/events',
        code: apiErr?.code,
        status: apiErr?.status,
        durationMs: Date.now() - startedAt,
      })
    } finally {
      setLoading(false)
    }
  }, [limit, projectId])

  useEffect(() => {
    let cancelled = false
    let channel: RealtimeChannel | null = null
    const { live, supabase } = getRuntime()

    void load()

    if (!live || !supabase || !projectId) return

    try {
      channel = supabase
        .channel(`square-shell-workspace-events-${projectId}`)
        .on(
          'postgres_changes' as any,
          { event: 'INSERT', schema: 'public', table: 'sq_workspace_agent_events' },
          async () => {
            if (cancelled) return
            try {
              await withSpan('sq_workspace_agent_events.refresh', 'db.query', load)
            } catch (err) {
              captureHookFailure('useWorkspaceEvents', 'realtime_failure', err, {
                table: 'sq_workspace_agent_events',
                route: '/api/workspace/events',
              })
            }
          },
        )
        .subscribe()
    } catch (err) {
      captureHookFailure('useWorkspaceEvents', 'realtime_failure', err, {
        table: 'sq_workspace_agent_events',
        route: '/api/workspace/events',
      })
    }

    return () => {
      cancelled = true
      if (channel && supabase) supabase.removeChannel(channel)
    }
  }, [load, projectId])

  return { events, workspaceIds, loading, error, refresh: load }
}
